import React, { useEffect, useState } from 'react';
import { adminApi, formatApiError } from './adminApi';
import { useAuth } from './AuthContext';
import {
  UserPlus,
  Trash2,
  KeyRound,
  Mail,
  ShieldCheck,
  ShieldOff,
  Pencil,
  X,
  Check,
} from 'lucide-react';

const RolePill = ({ role }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-[10px] font-['Inter'] uppercase tracking-wider ${
      role === 'admin'
        ? 'bg-[#83BF42]/15 text-[#83BF42] border border-[#83BF42]/30'
        : 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
    }`}
  >
    {role}
  </span>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const generatePassword = () => {
  // Memorable yet strong: word-####-word!
  const words = ['Lekki', 'Yaba', 'Ikeja', 'Marina', 'Surulere', 'Apapa', 'Sango', 'Ikoyi', 'Ojodu', 'Lagos'];
  const w1 = words[Math.floor(Math.random() * words.length)];
  let w2 = words[Math.floor(Math.random() * words.length)];
  if (w2 === w1) w2 = words[(words.indexOf(w1) + 1) % words.length];
  const n = 1000 + Math.floor(Math.random() * 9000);
  const sym = '!@#$&*'.charAt(Math.floor(Math.random() * 6));
  return `${w1}-${n}-${w2}${sym}`;
};

const AdminTeam = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'editor', password: generatePassword() });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'editor' });
  const [resetForId, setResetForId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetResult, setResetResult] = useState(null);

  const refresh = () => {
    setLoading(true);
    setErr('');
    adminApi
      .listUsers()
      .then(setUsers)
      .catch((e) => setErr(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteSubmitting(true);
    setErr('');
    try {
      await adminApi.createUser(inviteForm);
      setInviteResult({ email: inviteForm.email, password: inviteForm.password, role: inviteForm.role });
      setInviteForm({ email: '', name: '', role: 'editor', password: generatePassword() });
      refresh();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setInviteSubmitting(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({ name: u.name || '', role: u.role });
  };

  const saveEdit = async (id) => {
    try {
      await adminApi.updateUser(id, editForm);
      setEditingId(null);
      refresh();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const startReset = (u) => {
    setResetForId(u.id);
    setResetPassword(generatePassword());
    setResetResult(null);
  };

  const confirmReset = async () => {
    try {
      await adminApi.resetUserPassword(resetForId, resetPassword);
      const u = users.find((x) => x.id === resetForId);
      setResetResult({ email: u?.email, password: resetPassword });
      setResetForId(null);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteUser(u.id);
      refresh();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const handleReset2fa = async (u) => {
    if (u.id === me?.id) {
      window.alert("Use Settings → Turn off 2FA to reset your own 2FA.");
      return;
    }
    if (!window.confirm(
      `Reset 2FA for ${u.email}? They'll be forced to enrol again with a fresh authenticator on their next sign-in.`
    )) return;
    try {
      await adminApi.resetUser2fa(u.id);
      refresh();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-8 lg:p-10 text-white min-h-full" data-testid="admin-team-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-bold font-['Inter']">Team</p>
          <h1 className="text-white text-3xl md:text-4xl font-light font-['Inter'] mt-1">
            Admins &amp; editors
          </h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-1">
            Admins manage everything. Editors can only edit blog articles.
          </p>
        </div>
        <button
          onClick={() => { setShowInvite(true); setInviteResult(null); }}
          data-testid="team-invite-trigger"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#242326] text-sm font-['Inter'] font-medium hover:bg-[#76ad3a] transition"
        >
          <UserPlus className="w-4 h-4" /> Invite member
        </button>
      </header>

      {err && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm font-['Lato']" data-testid="team-error">
          {err}
        </div>
      )}

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowInvite(false)}>
          <div className="w-full max-w-md bg-[#15151A] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="team-invite-modal">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-white text-lg font-['Inter'] font-medium">Invite a teammate</h2>
              <button onClick={() => setShowInvite(false)} className="text-white/40 hover:text-white" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {inviteResult ? (
              <div className="p-5 space-y-4" data-testid="team-invite-result">
                <div className="px-4 py-3 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42]">
                  <p className="text-sm font-['Inter'] font-medium">User created!</p>
                  <p className="text-xs font-['Lato'] mt-1">
                    Send <strong>{inviteResult.email}</strong> these credentials securely (e.g. via 1Password, Slack DM, or in person):
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-white/60 text-xs font-['Inter'] uppercase tracking-wider">Email</p>
                  <code className="block w-full px-3 py-2 rounded-md bg-white/5 text-white text-sm font-mono">{inviteResult.email}</code>
                  <p className="text-white/60 text-xs font-['Inter'] uppercase tracking-wider mt-3">Temporary password</p>
                  <code className="block w-full px-3 py-2 rounded-md bg-white/5 text-white text-sm font-mono break-all">{inviteResult.password}</code>
                  <p className="text-white/60 text-xs font-['Inter'] uppercase tracking-wider mt-3">Role</p>
                  <RolePill role={inviteResult.role} />
                </div>
                <p className="text-white/40 text-xs font-['Lato']">
                  Tell them to change this password on first sign-in via Settings.
                </p>
                <button
                  onClick={() => setShowInvite(false)}
                  data-testid="team-invite-close"
                  className="w-full px-4 py-2.5 rounded-full bg-[#83BF42] text-[#242326] text-sm font-['Inter'] font-medium hover:bg-[#76ad3a]"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleInvite} className="p-5 space-y-4">
                <div>
                  <label className="block text-white/70 text-xs font-['Inter'] uppercase tracking-wider mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                    data-testid="team-invite-email"
                    className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-['Inter'] focus:outline-none focus:border-[#83BF42]"
                    placeholder="teammate@grouth.co"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-['Inter'] uppercase tracking-wider mb-1.5">Display name</label>
                  <input
                    type="text"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="team-invite-name"
                    className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-['Inter'] focus:outline-none focus:border-[#83BF42]"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-['Inter'] uppercase tracking-wider mb-1.5">Role</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
                    data-testid="team-invite-role"
                    className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-['Inter'] focus:outline-none focus:border-[#83BF42]"
                  >
                    <option value="editor">Editor — blog articles only</option>
                    <option value="admin">Admin — full access</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-['Inter'] uppercase tracking-wider mb-1.5">
                    Temporary password
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      required
                      minLength={8}
                      value={inviteForm.password}
                      onChange={(e) => setInviteForm((f) => ({ ...f, password: e.target.value }))}
                      data-testid="team-invite-password"
                      className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-mono focus:outline-none focus:border-[#83BF42]"
                    />
                    <button
                      type="button"
                      onClick={() => setInviteForm((f) => ({ ...f, password: generatePassword() }))}
                      className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs font-['Inter'] hover:bg-white/10"
                    >
                      Regenerate
                    </button>
                  </div>
                  <p className="text-white/30 text-[11px] font-['Lato'] mt-1.5">
                    Share this once. The user can change it after first sign-in.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={inviteSubmitting}
                  data-testid="team-invite-submit"
                  className="w-full px-4 py-2.5 rounded-full bg-[#83BF42] text-[#242326] text-sm font-['Inter'] font-medium hover:bg-[#76ad3a] disabled:opacity-50"
                >
                  {inviteSubmitting ? 'Creating…' : 'Create user'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetForId && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setResetForId(null)}>
          <div className="w-full max-w-md bg-[#15151A] border border-white/10 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="team-reset-modal">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-white text-lg font-['Inter'] font-medium">Reset password</h2>
              <button onClick={() => setResetForId(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-white/60 text-sm font-['Lato']">
                You&rsquo;ll need to share the new password securely with the user.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  data-testid="team-reset-password"
                  className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setResetPassword(generatePassword())}
                  className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs font-['Inter']"
                >
                  Regenerate
                </button>
              </div>
              <button
                onClick={confirmReset}
                disabled={resetPassword.length < 8}
                data-testid="team-reset-submit"
                className="w-full px-4 py-2.5 rounded-full bg-[#83BF42] text-[#242326] text-sm font-['Inter'] font-medium hover:bg-[#76ad3a] disabled:opacity-50"
              >
                Confirm reset
              </button>
            </div>
          </div>
        </div>
      )}

      {resetResult && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-sm font-['Lato']" data-testid="team-reset-result">
          New password for <strong>{resetResult.email}</strong>: <code className="px-2 py-0.5 rounded bg-black/40 font-mono">{resetResult.password}</code>
          <button onClick={() => setResetResult(null)} className="ml-3 text-[#83BF42]/70 hover:text-white" aria-label="Dismiss"><X className="w-3.5 h-3.5 inline" /></button>
        </div>
      )}

      {/* Users table */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="w-7 h-7 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm font-['Lato']">No team members yet.</div>
        ) : (
          <table className="w-full text-sm font-['Inter']">
            <thead className="bg-white/[0.03] text-white/40 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">User</th>
                <th className="px-5 py-3 text-left">Role</th>
                <th className="px-5 py-3 text-left">Joined</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.map((u) => (
                <tr key={u.id} data-testid={`team-row-${u.id}`} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#83BF42]/15 text-[#83BF42] flex items-center justify-center font-['Inter'] font-medium uppercase">
                        {(u.name || u.email).charAt(0)}
                      </div>
                      <div className="min-w-0">
                        {editingId === u.id ? (
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Display name"
                            className="px-2 py-1 rounded bg-white/10 text-white text-sm w-full"
                            data-testid={`team-edit-name-${u.id}`}
                          />
                        ) : (
                          <p className="text-white truncate">{u.name || u.email.split('@')[0]}</p>
                        )}
                        <p className="text-white/40 text-xs flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" /> {u.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {editingId === u.id ? (
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                        data-testid={`team-edit-role-${u.id}`}
                        className="px-2 py-1 rounded bg-white/10 text-white text-xs"
                      >
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <RolePill role={u.role} />
                    )}
                  </td>
                  <td className="px-5 py-4 text-white/40 text-xs font-['Lato']">{formatDate(u.created_at)}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {editingId === u.id ? (
                        <>
                          <button
                            onClick={() => saveEdit(u.id)}
                            data-testid={`team-edit-save-${u.id}`}
                            className="p-1.5 rounded text-[#83BF42] hover:bg-[#83BF42]/10"
                            aria-label="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1.5 rounded text-white/40 hover:bg-white/5"
                            aria-label="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(u)}
                            data-testid={`team-edit-${u.id}`}
                            className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5"
                            title="Edit role / name"
                            aria-label="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startReset(u)}
                            data-testid={`team-reset-${u.id}`}
                            className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5"
                            title="Reset password"
                            aria-label="Reset password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          {u.id !== me?.id && (
                            <button
                              onClick={() => handleReset2fa(u)}
                              data-testid={`team-reset-2fa-${u.id}`}
                              className={`p-1.5 rounded hover:bg-white/5 ${u.totp_enabled ? 'text-[#83BF42] hover:text-[#A8D96C]' : 'text-white/30 hover:text-white/50'}`}
                              title={u.totp_enabled ? `Reset 2FA (currently ON, ${u.backup_codes_remaining || 0} backup codes left)` : '2FA not yet set up'}
                              aria-label="Reset 2FA"
                              disabled={!u.totp_enabled}
                            >
                              {u.totp_enabled ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                            </button>
                          )}
                          {u.id !== me?.id && (
                            <button
                              onClick={() => handleDelete(u)}
                              data-testid={`team-delete-${u.id}`}
                              className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10"
                              title="Delete user"
                              aria-label="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {u.id === me?.id && (
                            <span
                              className="px-2 py-0.5 rounded text-[10px] text-white/30 font-['Inter'] flex items-center gap-1"
                              title="That's you"
                            >
                              <ShieldCheck className="w-3 h-3" /> You
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminTeam;
